import { Controller } from '@hotwired/stimulus'
import { PaginationSection } from './request_form/pagination'
import { ParametersSection, extractRequestIndex } from './request_form/parameters'
import { ConditionsSection } from './request_form/conditions'
import { ResourceTypeSection } from './request_form/resource_type'
import { ResponseExampleSection } from './request_form/response_example'
import { loadEndpointsMetadata, attributesForNode, removePlaceholderOption, type EndpointType, type AttributeType } from './request_form/endpoint_metadata'
import { ROOT_NODE, displayAttribute } from './request_form/display'
import { UrlPreviewSection } from './request_form/url_preview'

export default class extends Controller<any> {
  static targets = [
    'endpoint',
    'kind',
    'kindOptions',
    'kindHidden',
    'node',
    'nodeInput',
    'nodeHidden',
    'attributeSelect',
    'responseExample',
    'parametersCard',
    'parametersDescription',
    'parametersFields',
    'parametersEmptyState',
    'requestUrlPreview',
    'parameterTemplate',
    'inputTemplate',
    'valueInput',
    'condition',
    'conditionValueType',
    'paginationSection',
    'paginationMode',
    'paginationInput',
    'paginationNoPaginationHint',
    'paginationLinkFields',
    'paginationPathSelect',
    'paginationBuildLinkFields',
    'paginationQueryParamSelect',
    'paginationStrategySelect',
    'paginationIterationFields',
    'paginationStartInput',
    'paginationStepInput',
    'paginationValuePathFields',
    'paginationValuePathSelect'
  ]

  static values = {
    existingParameters: Array,
    baseUrl: String
  }

  declare endpointTarget: HTMLSelectElement

  declare kindTargets: HTMLInputElement[]
  declare kindOptionsTarget: HTMLElement
  declare kindHiddenTarget: HTMLInputElement
  declare hasKindHiddenTarget: boolean
  declare hasKindOptionsTarget: boolean

  declare nodeTarget: HTMLSelectElement
  declare nodeInputTarget: HTMLElement
  declare nodeHiddenTarget: HTMLInputElement
  declare hasNodeInputTarget: boolean
  declare hasNodeHiddenTarget: boolean

  declare attributeSelectTargets: HTMLSelectElement[]
  declare valueInputTargets: HTMLInputElement[]
  declare conditionTargets: HTMLElement[]
  declare conditionValueTypeTargets: HTMLInputElement[]

  declare paginationSectionTarget: HTMLElement
  declare hasPaginationSectionTarget: boolean
  declare paginationModeTargets: HTMLInputElement[]
  declare paginationInputTarget: HTMLInputElement
  declare hasPaginationInputTarget: boolean
  declare paginationNoPaginationHintTarget: HTMLElement
  declare hasPaginationNoPaginationHintTarget: boolean
  declare paginationLinkFieldsTarget: HTMLElement
  declare hasPaginationLinkFieldsTarget: boolean
  declare paginationPathSelectTarget: HTMLSelectElement
  declare hasPaginationPathSelectTarget: boolean
  declare paginationBuildLinkFieldsTarget: HTMLElement
  declare hasPaginationBuildLinkFieldsTarget: boolean
  declare paginationQueryParamSelectTarget: HTMLSelectElement
  declare hasPaginationQueryParamSelectTarget: boolean
  declare paginationStrategySelectTarget: HTMLSelectElement
  declare hasPaginationStrategySelectTarget: boolean
  declare paginationIterationFieldsTarget: HTMLElement
  declare hasPaginationIterationFieldsTarget: boolean
  declare paginationStartInputTarget: HTMLInputElement
  declare hasPaginationStartInputTarget: boolean
  declare paginationStepInputTarget: HTMLInputElement
  declare hasPaginationStepInputTarget: boolean
  declare paginationValuePathFieldsTarget: HTMLElement
  declare hasPaginationValuePathFieldsTarget: boolean
  declare paginationValuePathSelectTarget: HTMLSelectElement
  declare hasPaginationValuePathSelectTarget: boolean

  declare responseExampleTarget: HTMLElement
  declare hasResponseExampleTarget: boolean
  declare parametersCardTarget: HTMLElement
  declare hasParametersCardTarget: boolean
  declare parametersDescriptionTarget: HTMLElement
  declare parametersFieldsTarget: HTMLElement
  declare parametersEmptyStateTarget: HTMLElement
  declare requestUrlPreviewTarget: HTMLElement
  declare parameterTemplateTarget: HTMLTemplateElement
  declare inputTemplateTarget: HTMLTemplateElement
  declare existingParametersValue: Array<{ name: string, value: string, id?: string, errors?: string[] }>
  declare baseUrlValue: string

  private endpointsData: Record<string, EndpointType> = {}
  private currentEndpoint: EndpointType | null = null
  private parametersSection?: ParametersSection
  private paginationSection?: PaginationSection
  private resourceTypeSection?: ResourceTypeSection
  private requestUrlPreviewSection?: UrlPreviewSection

  connect (): void {
    this.endpointsData = loadEndpointsMetadata(this.endpointTarget)
    this.syncParametersCardVisibility()
    this.restoreEndpointState()
    this.updateResponseExample()
  }

  endpointChanged (): void {
    removePlaceholderOption(this.endpointTarget)
    this.conditions.removeNonPersisted()
    this.conditions.clearValues()
    this.refresh()
    this.resetPagination()
  }

  kindChanged (): void {
    this.conditions.clearValues()

    if (this.currentEndpoint != null) {
      this.resourceType.autoselect(this.currentEndpoint)
      const node = this.resourceType.updateNodes(this.currentEndpoint)
      this.conditions.updateAll(node, this.attributesForNode(node))
    }

    this.syncParametersCardVisibility()
    this.resetPagination()
  }

  nodeChanged (): void {
    this.conditions.clearValues()

    const node = this.resourceType.selectedNode()
    this.conditions.updateAll(node, this.attributesForNode(node))
    this.resourceType.syncNodeInputsFor(this.currentEndpoint)
    this.resetPagination()
  }

  paginationModeChanged (): void {
    this.applyPagination()
  }

  paginationPathChanged (): void {
    this.applyPagination()
  }

  paginationBuildLinkFieldChanged (): void {
    this.applyPagination()
  }

  attributeChanged (event: Event): void {
    const select = event.target as HTMLSelectElement
    if (select == null) return

    this.conditions.syncValueType(select)
  }

  conditionAdded (): void {
    setTimeout(() => {
      const newCondition = this.attributeSelectTargets[this.attributeSelectTargets.length - 1]
      if (newCondition == null) return

      const node = this.resourceType.selectedNode()
      this.conditions.syncValueType(newCondition)
      this.conditions.updateOptions(newCondition, node, this.attributesForNode(node))
    })
  }

  private get parameters (): ParametersSection {
    if (this.parametersSection == null) {
      this.parametersSection = new ParametersSection(
        {
          card: this.hasParametersCardTarget ? this.parametersCardTarget : undefined,
          description: this.parametersDescriptionTarget,
          fields: this.parametersFieldsTarget,
          emptyState: this.parametersEmptyStateTarget,
          parameterTemplate: this.parameterTemplateTarget,
          inputTemplate: this.inputTemplateTarget
        },
        extractRequestIndex(this.endpointTarget.name),
        () => this.updateRequestUrlPreview()
      )
      this.parametersSection.loadExistingFromServer(this.existingParametersValue)
    }

    return this.parametersSection
  }

  private get pagination (): PaginationSection {
    if (this.paginationSection == null) {
      this.paginationSection = new PaginationSection({
        section: this.hasPaginationSectionTarget ? this.paginationSectionTarget : undefined,
        modeRadios: this.paginationModeTargets,
        input: this.hasPaginationInputTarget ? this.paginationInputTarget : undefined,
        hint: this.hasPaginationNoPaginationHintTarget ? this.paginationNoPaginationHintTarget : undefined,
        linkFields: this.hasPaginationLinkFieldsTarget ? this.paginationLinkFieldsTarget : undefined,
        pathSelect: this.hasPaginationPathSelectTarget ? this.paginationPathSelectTarget : undefined,
        buildLinkFields: this.hasPaginationBuildLinkFieldsTarget ? this.paginationBuildLinkFieldsTarget : undefined,
        queryParamSelect: this.hasPaginationQueryParamSelectTarget ? this.paginationQueryParamSelectTarget : undefined,
        strategySelect: this.hasPaginationStrategySelectTarget ? this.paginationStrategySelectTarget : undefined,
        iterationFields: this.hasPaginationIterationFieldsTarget ? this.paginationIterationFieldsTarget : undefined,
        startInput: this.hasPaginationStartInputTarget ? this.paginationStartInputTarget : undefined,
        stepInput: this.hasPaginationStepInputTarget ? this.paginationStepInputTarget : undefined,
        valuePathFields: this.hasPaginationValuePathFieldsTarget ? this.paginationValuePathFieldsTarget : undefined,
        valuePathSelect: this.hasPaginationValuePathSelectTarget ? this.paginationValuePathSelectTarget : undefined
      })
    }

    return this.paginationSection
  }

  private get conditions (): ConditionsSection {
    return new ConditionsSection({
      containers: this.conditionTargets,
      attributeSelects: this.attributeSelectTargets,
      valueInputs: this.valueInputTargets,
      valueTypeInputs: this.conditionValueTypeTargets
    })
  }

  private get resourceType (): ResourceTypeSection {
    if (this.resourceTypeSection == null) {
      this.resourceTypeSection = new ResourceTypeSection({
        kindRadios: this.kindTargets,
        kindOptions: this.hasKindOptionsTarget ? this.kindOptionsTarget : undefined,
        kindHidden: this.hasKindHiddenTarget ? this.kindHiddenTarget : undefined,
        nodeSelect: this.nodeTarget,
        nodeInput: this.hasNodeInputTarget ? this.nodeInputTarget : undefined,
        nodeHidden: this.hasNodeHiddenTarget ? this.nodeHiddenTarget : undefined
      })
    }

    return this.resourceTypeSection
  }

  private get responseExample (): ResponseExampleSection {
    return new ResponseExampleSection(this.responseExampleTarget)
  }

  private get requestUrlPreview (): UrlPreviewSection {
    if (this.requestUrlPreviewSection == null) {
      this.requestUrlPreviewSection = new UrlPreviewSection(this.requestUrlPreviewTarget)
    }

    return this.requestUrlPreviewSection
  }

  private refresh (preserveValues: boolean = false): void {
    this.currentEndpoint = this.endpointsData[this.endpointTarget.value] ?? null
    if (this.currentEndpoint == null) return

    this.resourceType.autoselect(this.currentEndpoint)
    this.parameters.update(this.currentEndpoint.parameters ?? [], preserveValues)

    const node = this.resourceType.updateNodes(this.currentEndpoint)
    this.conditions.updateAll(node, this.attributesForNode(node))

    this.updateResponseExample()
    this.updatePaginationPathOptions()
    this.updatePaginationQueryParamOptions()
    this.syncParametersCardVisibility()
    this.updateRequestUrlPreview()
  }

  private restoreEndpointState (): void {
    if (this.endpointTarget.value === '') return
    removePlaceholderOption(this.endpointTarget)
    this.refresh(true)
  }

  private attributesForNode (node: string): AttributeType[] {
    return attributesForNode(this.currentEndpoint, node)
  }

  private updatePaginationPathOptions (): void {
    const attributes = this.attributesForNode(ROOT_NODE)
    this.pagination.updatePathOptions(attributes, path => displayAttribute(ROOT_NODE, path))
    this.pagination.updateValuePathOptions(attributes, path => displayAttribute(ROOT_NODE, path))
  }

  private updatePaginationQueryParamOptions (): void {
    const params = (this.currentEndpoint?.parameters ?? []).filter(p => p.in === 'query')
    this.pagination.updateQueryParamOptions(params)
  }

  private updateResponseExample (): void {
    if (this.currentEndpoint == null) return

    this.responseExample.update(this.currentEndpoint.response_example)
  }

  private applyPagination (): void {
    this.pagination.apply()
    this.updateRequestUrlPreview()
  }

  private resetPagination (): void {
    this.pagination.reset()
    this.updateRequestUrlPreview()
  }

  private updateRequestUrlPreview (): void {
    this.requestUrlPreview.update({
      baseUrl: this.baseUrlValue,
      endpoint: this.currentEndpoint,
      parameterValues: this.parameters.currentValues(),
      pagination: this.pagination.currentConfig()
    })
  }

  private syncParametersCardVisibility (): void {
    const isCollection = this.resourceType.selectedKind() === 'collection'
    const noEndpoint = this.endpointTarget.value === ''
    const hasParameters = (this.currentEndpoint?.parameters ?? []).length > 0
    const hasPagination = this.hasPaginationSectionTarget && isCollection

    this.pagination.toggleSection(isCollection)
    this.parameters.toggleCard(!noEndpoint && (hasParameters || hasPagination))
  }
}
